import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CustomerContactsState } from "src/types";

import { Dispatch, StoreState } from "../../store";
import Autocomplete from "../Autocomplete";

const categories = [
  { label: "Kontaktkanal", value: "contact_channel" },
  { label: "Kundenalter", value: "customer_age" },
  { label: "CLV", value: "customer_clv" },
  { label: "Churn Score", value: "customer_churn_score" },
  { label: "PLZ", value: "customer_postal_code" },
];

const initialCategories = [categories[0], categories[2], categories[3]];

interface Props {
  customerContacts: CustomerContactsState;
}

const useStyles = makeStyles(() => ({
  topsFlopsTable: {
    borderCollapse: "collapse",
  },
  topsFlopsTableRow: {
    borderBottom: "1px solid #cccccc",
  },
  topsFlopsTableHeader: {
    borderBottom: "1px solid #cccccc",
    textAlign: "left",
    paddingRight: 15,
  },
  topsFlopsTableCell: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingRight: 15,
    fontSize: "1rem",
  },
  tableNotShown: {
    color: "#888888",
    fontStyle: "italic",
    fontSize: "1rem",
    textAlign: "center",
    marginTop: "20px",
  },
  "@global": {
    "#categories_selector > div > div.MuiFormLabel-root": {
      transform: "translate(0, 5px) scale(0.75)",
      transformOrigin: "left",
    },
    ".MuiChip-labelSmall": {
      fontSize: "1rem",
    },
  },
}));

const TopCombinations: React.FC<Props> = ({ customerContacts }: Props) => {
  const classes = useStyles();
  const [selectedNbaUuid, setSelectedNbaUuid] = useState<string>();
  const [selectedCategories, setSelectedCategories] = useState(
    initialCategories
  );
  const [initialFetch, setInitialFetch] = useState(true);
  const [notEnoughCombinations, setNotEnoughCombinations] = useState(false);
  const dispatch = useDispatch<Dispatch>();
  const customerContactsTopFlopCombinations = useSelector(
    (state: StoreState) => state.customerContactsTopFlopCombinations
  );
  const nextBestActivities = useSelector(
    (state: StoreState) => state.nextBestActivities
  );
  useEffect(() => {
    if (selectedNbaUuid && initialFetch) {
      dispatch.customerContactsTopFlopCombinations.fetchCustomerContactsTopFlopCombinations(
        {
          nba_uuid: selectedNbaUuid,
          categories: initialCategories.map((category) => category.value),
        }
      );
      setInitialFetch(false);
    }
  }, [dispatch, initialFetch, selectedNbaUuid]);

  useEffect(() => {
    dispatch.nextBestActivities.fetchNextBestActivities(null);
  }, [dispatch]);

  useEffect(() => {
    setSelectedNbaUuid(
      nextBestActivities.find(
        (nba) =>
          nba.name ===
          customerContacts.countNbaTopTenByOfferAddressed.offerAddressed
            .nbaNames[0]
      )?.uuid
    );
  }, [
    customerContacts.countNbaTopTenByOfferAddressed.offerAddressed.nbaNames,
    nextBestActivities,
  ]);

  useEffect(() => {
    if (customerContactsTopFlopCombinations.combinations.length < 10) {
      setNotEnoughCombinations(true);
    } else {
      setNotEnoughCombinations(false);
    }
  }, [customerContactsTopFlopCombinations.combinations.length]);

  const handleChangeCategories = (
    categories: {
      label: string;
      value: string;
    }[]
  ) => {
    setSelectedCategories(categories);
    if (selectedNbaUuid) {
      dispatch.customerContactsTopFlopCombinations.fetchCustomerContactsTopFlopCombinations(
        {
          nba_uuid: selectedNbaUuid,
          categories: categories.map((category) => category.value),
        }
      );
    }
  };

  const getTableHeaders = () => {
    const labels = selectedCategories.map((category) => category.label);
    return (
      <tr>
        {labels.map((label) => (
          <th className={classes.topsFlopsTableHeader} key={label}>
            {label}
          </th>
        ))}
        <th className={classes.topsFlopsTableHeader}>Score</th>
      </tr>
    );
  };

  const getTableCellContents = (which: "top" | "flop") => {
    let combinations = customerContactsTopFlopCombinations.combinations;
    combinations =
      which === "top"
        ? combinations.slice(0, 5)
        : combinations.slice(-5).reverse();
    return combinations.map((combination, combinationIndex) => ({
      nodes: selectedCategories
        .map((category) => category.value)
        .concat(["score"])
        .reduce<React.ReactNode[]>((stringifiedArray, value) => {
          return stringifiedArray.concat([
            <div key={`${which}_${value}_${combinationIndex}`}>
              {combination[value] as string | number}
            </div>,
          ]);
        }, []),
      score: combination.score,
    }));
  };

  const handleChangeNba = (
    event: React.ChangeEvent<{
      value: string;
    }>
  ) => {
    setSelectedNbaUuid(event.target.value);
    dispatch.customerContactsTopFlopCombinations.fetchCustomerContactsTopFlopCombinations(
      {
        nba_uuid: event.target.value,
        categories: selectedCategories.map((category) => category.value),
      }
    );
  };

  const getBarScore = (
    score: number,
    normalizeTo: number,
    which: "top" | "flop"
  ) => {
    if (which === "top") {
      return parseInt(((score / normalizeTo) * 100).toString());
    } else {
      if (normalizeTo === 0) {
        return parseInt((((normalizeTo + 1) / (score + 1)) * 100).toString());
      } else {
        return parseInt(((normalizeTo / score) * 100).toString());
      }
    }
  };

  const getResultTable = (which: "top" | "flop") => {
    const results = getTableCellContents(which);
    return (
      <table className={classes.topsFlopsTable}>
        {getTableHeaders()}
        {results.map((result, index) => {
          const barColor =
            which === "top"
              ? "rgba(191, 210, 120, 0.7)"
              : "rgba(220, 150, 150, 0.7)";
          return (
            <tr
              className={classes.topsFlopsTableRow}
              style={{
                background: `linear-gradient(90deg, ${barColor} ${getBarScore(
                  result.score,
                  results[0].score,
                  which
                )}%, transparent ${getBarScore(
                  result.score,
                  results[0].score,
                  which
                )}% 100%`,
              }}
              key={`${which}_${index}`}
            >
              {result.nodes.map((property, propIndex) => (
                <td
                  className={classes.topsFlopsTableCell}
                  key={`${which}_${index}_${propIndex}`}
                >
                  {property}
                </td>
              ))}
            </tr>
          );
        })}
      </table>
    );
  };

  return (
    <>
      {selectedNbaUuid && (
        <Grid container>
          <Grid item xs={12}>
            <Grid container alignItems="flex-end">
              <Grid item xs={3}>
                <FormControl style={{ width: 300, marginBottom: 8 }}>
                  <InputLabel>Beratungsfokus</InputLabel>
                  <Select value={selectedNbaUuid} onChange={handleChangeNba}>
                    {nextBestActivities.map((nba) => (
                      <MenuItem key={nba.uuid} value={nba.uuid}>
                        {nba.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={5}>
                <Autocomplete
                  id="categories_selector"
                  label="Kategorien"
                  size="small"
                  options={categories}
                  value={selectedCategories}
                  onChange={handleChangeCategories}
                  placeholder={
                    !selectedCategories.length
                      ? "Bitte Kategorien hinzufügen"
                      : ""
                  }
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {selectedCategories.length ? (
              notEnoughCombinations ? (
                <div className={classes.tableNotShown}>
                  weniger als 10 Kombinationen vorhanden, bitte Selektion
                  anpassen.
                </div>
              ) : (
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography variant="h6" style={{ marginTop: "20px" }}>
                          Top 5
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        {getResultTable("top")}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography variant="h6" style={{ marginTop: "20px" }}>
                          Flop 5
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        {getResultTable("flop")}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )
            ) : (
              <div className={classes.tableNotShown}>
                keine Kategorien ausgewählt
              </div>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default TopCombinations;



// WEBPACK FOOTER //
// ./src/components/NbaEvaluation/TopFlopCombinations.tsx